import { HttpService, QueryHandler, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { AccountancyValidationStatus, AggregatedEmailStatusCode, DataExchangeKind, DataExchangeStatus, EmailDeliveryStatus, JpkVatSubkind, PackageOfDocumentsSubkind } from '@skylab-shared/dataModel';
import { Mutable } from '@skylab-shared/types';
import { head } from '@skylab-shared/utils';
import { Transform, Type } from 'class-transformer';
import { PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from './../../common/query/paged-collection-query';

export interface AccountancyMessage {
    text: string;
    severity: DataExchangeStatus;
    redirectState: any;
}

export interface AccountancyValidation {
    messageType: AccountancyValidationStatus;
    severity: DataExchangeStatus;
    placeholders: string[];
}

export type AccountancyGridItem = Omit<AccountancyGridItemDTO, 'uris'> & {
    createdAt: Date;
    kind: DataExchangeKind;
    jpkVatSubKind: JpkVatSubkind | null;
    number: number;
    isNexoExchange: boolean;
    uri: string | null;
};

class AccountancyGridItemDTO {
    public attachments: string[];
    public uris: string[];
    public messages: AccountancyMessage[];
    public validations: AccountancyValidation[];

    @Transform(transformDTODate, { toClassOnly: true }) public readonly from: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public generated: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly created: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly to: Date;

    public readonly id: number;
    public readonly kind: string;
    public readonly seqNo: number;
    public status: DataExchangeStatus;
    public aggregatedEmailStatusCode: AggregatedEmailStatusCode;
    @Type(() => EmailDeliveryStatus) public emailStatuses: EmailDeliveryStatus[];

}

export class AccountancyGridQuery extends PagedCollectionQuery<AccountancyGridItem> { }

const KIND_MAPPINGS: Partial<Record<DataExchangeKind, string[]>> = {
    [DataExchangeKind.JPK_VAT]: Object.values(JpkVatSubkind),
    [DataExchangeKind.PackageOfDocuments]: Object.values(PackageOfDocumentsSubkind),
};

export class AccountancyGridQueryHandlerBase implements QueryHandler<AccountancyGridQuery, PagedCollectionQueryResult<AccountancyGridItem>> {
    private static Url = TaggedUrl.join('accounting', [Tags.Api]);

    public constructor(private readonly http: HttpService) { }

    public async handleAsync(query: AccountancyGridQuery): Promise<PagedCollectionQueryResult<AccountancyGridItem>> {
        const { kind, ...restParams } = query.params;
        const typedKind = kind as DataExchangeKind | undefined;
        const params = {
            ...restParams,
            ...(typedKind ? { kind: KIND_MAPPINGS[typedKind] ?? [typedKind] } : {})
        } as const;

        const { data } = await this.http.get<Mutable<PagedCollectionQueryResult<AccountancyGridItemDTO>>>(AccountancyGridQueryHandlerBase.Url, {
            params,
            dtoObject: new PagedCollectionQueryResultDTO<AccountancyGridItemDTO>(AccountancyGridItemDTO)
        });

        const items = data.items.map(item => {
            const isJpkVatSubkind = Object.values(JpkVatSubkind).includes(item.kind as JpkVatSubkind);
            const isPackageOfDocumentsSubkind = Object.values(PackageOfDocumentsSubkind).includes(item.kind as PackageOfDocumentsSubkind);
            return {
                id: item.id,
                createdAt: item.created,
                created: item.created,
                seqNo: item.seqNo,
                messages: item.messages,
                validations: item.validations,
                kind: isJpkVatSubkind
                    ? DataExchangeKind.JPK_VAT
                    : isPackageOfDocumentsSubkind
                        ? DataExchangeKind.PackageOfDocuments
                        : <DataExchangeKind>item.kind,
                jpkVatSubKind: isJpkVatSubkind || isPackageOfDocumentsSubkind
                    ? <JpkVatSubkind>item.kind
                    : null,
                number: item.seqNo,
                status: item.status,
                attachments: item.attachments,
                uri: item.uris?.length ? head(item.uris) : null,
                isNexoExchange: item.kind === DataExchangeKind.NexoOnline,
                from: item.from,
                to: item.to,
                generated: item.generated,
                aggregatedEmailStatusCode: item.aggregatedEmailStatusCode,
                emailStatuses: item.emailStatuses.map(s => ({
                    recipient: s.recipient,
                    status: s.status,
                    description: s.description,
                    updatedAt: s.updatedAt
                }))
            };
        });

        return {
            items: items,
            totalItemsCount: data.totalItemsCount
        };
    }
}
